import React, { useContext, useState } from 'react'
import { Formik } from 'formik'
import { Button, Form, NotificationInline, NotificationManagerContext,
   Spacer, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { CloseIcon } from 'assets/icons'
import { Input } from 'src/components/ConnectedForm'
import { useUpdatePage } from 'src/graphql/hooks'
import PageContext from '~/Page/libraries/context'

const PageSettingsUrlEdit = ({ onCloseButtonClick }) => {
   const history = useHistory()
   const page = useContext(PageContext)
   const { updatePage } = useUpdatePage()
   const hasGlobalUrl = page.globalUrls.length > 0

   const [globalUrlInputIsVisible, setGlobalUrlInputIsVisible] = useState(hasGlobalUrl)
   const [error, setError] = useState(null)
   const { showNotification } = useContext(NotificationManagerContext)

   const handleSaveInitiative = async (values, actions) => {
      setError(null)

      try {
         const result = await updatePage({ id: page.id }, {
            globalUrls: values.globalUrls?.trim() !== '' ? [values.globalUrls] : [],
            slug: values.slug
         })

         const updatedPage = result.data.updatePage

         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })

         // We assume that slug has been changed and update the route
         if (updatedPage.globalUrls.length) {
            window.location.assign(`/${updatedPage.globalUrls[0]}/settings`)
         } else {
            history.push(`/pages/${updatedPage.slug}/settings`)
         }
      } catch (err) {
         setError(err)
      } finally {
         actions.setSubmitting(false)
      }
   }

   return (
      <View width="100%" height="100%">
         <View
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom="1px solid #eee"
            height="54px"
            padding="0px 18px">
            <Text size="2" weight="medium">
               Edit Page URL
            </Text>
            <View
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               onClick={onCloseButtonClick}>
               <CloseIcon width="18px" height="18px" />
            </View>
         </View>
         <Formik
            initialValues={{
               globalUrls: page.globalUrls[0] || '',
               slug: page.slug
            }}
            onSubmit={handleSaveInitiative}
            render={({ handleSubmit, isSubmitting }) => (
               <Form
                  elementAppearance="plain"
                  elementBackgroundColor="#eee"
                  elementFocusBackgroundColor="#f3f3f3"
                  onSubmit={handleSubmit}>
                  <View
                     position="relative[a-b] absolute[c-f]"
                     width="100%"
                     height="calc(100% - 108px)"
                     scroll="on">
                     {error && (
                        <NotificationInline
                           paddingVertical="12px"
                           paddingHorizontal="18px"
                           textSize="1.5"
                           type="error"
                           title="Error"
                           message={error.graphQLErrors.map(e => e.message).join(', ') || error.message}
                        />
                     )}
                     <View width="100%" padding="18px">
                        <Text size="2" weight="medium">
                           Default Page URL
                        </Text>
                        <Text size="1.5" color="#999">
                           By default, this Page is accessible through the following URL:
                        </Text>
                        <Spacer size="2" />
                        <View
                           display="flex"
                           height="30px"
                           alignItems="center">
                           <View
                              flex="0 0 auto"
                              display="flex"
                              height="30px"
                              alignItems="center"
                              marginRight="12px">
                              <Text size="1.5" color="#999">
                                 domain.com/pages/
                              </Text>
                           </View>
                           <View flex="1 1 auto">
                              <Input size="sm" name="slug" required />
                           </View>
                        </View>
                        <Spacer size="3" />
                        <View
                           display="flex"
                           width="100%"
                           borderTop="1px solid #eee"
                           paddingTop="18px">
                           <View flex="1 1 auto">
                              <Text size="2" weight="medium">
                                 Full Custom URL
                              </Text>
                              <Text size="1.5" color="#999">
                                 {'In addition to the default URL above, you can set a fully customized URL for this Page'}
                              </Text>
                              {!globalUrlInputIsVisible && (
                                 <Text size="1.5" color="#aaa">
                                    (i.e., yourdomain.com/custom)
                                 </Text>
                              )}
                           </View>
                           {!globalUrlInputIsVisible && (
                              <View flex="0 0 auto" paddingLeft="18px">
                                 <Button
                                    onClick={() => setGlobalUrlInputIsVisible(true)}
                                    name="Set"
                                    color="#eee"
                                    size="sm"
                                    textColor="#333"
                                 />
                              </View>
                           )}
                        </View>
                        <Spacer size="2" />
                        {globalUrlInputIsVisible && (
                           <View
                              display="flex"
                              height="30px"
                              alignItems="center">
                              <View
                                 flex="0 0 auto"
                                 display="flex"
                                 height="30px"
                                 alignItems="center"
                                 marginRight="12px">
                                 <Text size="1.5" color="#999">
                                    domain.com/
                                 </Text>
                              </View>
                              <View flex="1 1 auto">
                                 <Input
                                    size="sm"
                                    name="globalUrls"
                                    placeholder="custom"
                                 />
                              </View>
                           </View>
                        )}
                     </View>
                  </View>
                  <View
                     position="relative[a-b] absolute[c-f]"
                     bottom="0px"
                     left="0px"
                     width="100%"
                     display="flex"
                     justifyContent="flex-end"
                     alignItems="center"
                     borderTop="1px solid #eee"
                     height="54px"
                     padding="0px 12px">
                     <Button
                        type="submit"
                        name="Save Changes"
                        mode={isSubmitting ? 'loading' : 'normal'}
                     />
                  </View>
               </Form>
            )}
         />
      </View>
   )
}

PageSettingsUrlEdit.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired
}

export default PageSettingsUrlEdit
